/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { LuisId } from 'src/luis/types/Resource';

/**
 * Defines a unified way to create urls for services.
 */
export const urlBuilder = () => {
    let url: string = '';
    const queryParams = new URLSearchParams();

    return {
        /**
         * Sets the base url to use for building the url.
         *
         * @param baseUrl The base url for the url to call.
         */
        baseUrl(baseUrl: string) {
            url = baseUrl;

            return this;
        },

        /**
         * Sets the application id to use in the url.
         *
         * @param appId The application id to use.
         */
        app(appId?: LuisId) {
            url = appId ? `${url}/apps/${appId}` : `${url}/apps`;

            return this;
        },

        /**
         * Sets the project id to use in the url.
         *
         * @param projectId The project id to use.
         */
        project(projectId?: LuisId) {
            url = projectId ? `${url}/projects/${projectId}` : `${url}/projects`;

            return this;
        },

        /**
         * Sets the model name to use in the url.
         *
         * @param modelName The model name to use.
         */
        model(modelName?: string) {
            url = modelName ? `${url}/models/${modelName}` : `${url}/models`;

            return this;
        },

        /**
         * Sets the version id to use in the url.
         *
         * @param versionId The version id to use.
         */
        version(versionId?: string) {
            url = versionId ? `${url}/versions/${versionId}` : `${url}/versions`;

            return this;
        },

        /**
         * Sets the resource name and id to use in the url.
         *
         * @param name The resource name to use.
         * @param id The resource id to use.
         */
        resource(name: string, id?: LuisId) {
            url = id ? `${url}/${name}/${id}` : `${url}/${name}`;

            return this;
        },

        /**
         * Adds a query parameter to the url.
         *
         * @param name The name of the query parameter.
         * @param value The value to use for the parameter.
         */
        queryParam(name: string, value: string) {
            queryParams.append(name, value);

            return this;
        },

        /**
         * Returns the built url.
         */
        build(): string {
            if (Array.from(queryParams.keys()).length > 0) {
                url = `${url}?${queryParams.toString()}`;
            }

            return url;
        }
    };
};
